import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import timoWaving from '@/assets/Timowavingg.webp';

function detectInAppBrowser() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  const isFB = /FBAN|FBAV|FB_IAB|FBIOS|FBDV/i.test(ua);
  const isIG = /Instagram/i.test(ua);
  if (!isFB && !isIG) return null;
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);
  const isAndroid = /Android/i.test(ua);
  return {
    source: isFB ? 'facebook' : 'instagram',
    platform: isIOS ? 'ios' : isAndroid ? 'android' : 'other',
  };
}

function capturePH(name, props) {
  try {
    if (typeof window !== 'undefined' && window.posthog?.capture) {
      window.posthog.capture(name, props);
    }
  } catch {
    /* ignore */
  }
}

const LANG_LABELS = { lt: 'Lietuvių', en: 'English', ru: 'Русский', es: 'Español', fr: 'Français' };

export default function InAppBrowserOverlay() {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [detected, setDetected] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [iosCopied, setIosCopied] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const copyUrlToClipboard = async (url) => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch {
      /* fall through */
    }
    if (!ok) {
      try {
        const el = document.createElement('textarea');
        el.value = url;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        ok = true;
      } catch {
        /* ignore */
      }
    }
    return ok;
  };

  useEffect(() => {
    // Never nag someone who just came back from Stripe. A post-payment redirect
    // carries ?session_id=cs_..., and telling a paying customer to switch
    // browsers at that exact moment loses the session they just paid for.
    // Reactive on location (not mount-only) so it clears itself once the
    // recovery redirect in App.jsx moves them on to /success.
    // The whole /fb funnel is also exempt: it is built to work inside the ads
    // webview and does its own breakout at the checkout hand-off, so an overlay
    // there would interrupt paid traffic on the way to paying.
    // Everywhere else the overlay stays: Google OAuth genuinely breaks inside
    // Facebook and Instagram webviews, so the nudge earns its keep.
    const sessionId = new URLSearchParams(location.search || '').get('session_id');
    const onFbFunnel = (location.pathname || '').startsWith('/fb');
    if (
      onFbFunnel ||
      location.pathname === '/success' ||
      (sessionId && sessionId.startsWith('cs_'))
    ) {
      setDetected(null);
      return;
    }

    const d = detectInAppBrowser();
    if (!d) {
      setDetected(null);
      return;
    }
    try {
      if (sessionStorage.getItem('ttt_iab_dismissed') === '1') {
        setDismissed(true);
      }
    } catch {
      /* ignore */
    }
    setDetected(d);
    capturePH('inapp_browser_overlay_shown', {
      source: d.source,
      platform: d.platform,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!langOpen) return;
    const onDown = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [langOpen]);

  if (!detected || dismissed) return null;

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const sourceLabel = detected.source === 'facebook' ? 'Facebook' : 'Instagram';

  const handleOpenAndroid = () => {
    capturePH('inapp_browser_overlay_open_tapped', {
      source: detected.source,
      platform: 'android',
    });
    const stripped = currentUrl.replace(/^https?:\/\//, '');
    window.location.href = `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;end`;
  };

  const handleIosCopyAndJump = async () => {
    capturePH('inapp_browser_overlay_open_tapped', {
      source: detected.source,
      platform: 'ios',
    });
    const ok = await copyUrlToClipboard(currentUrl);
    if (ok) {
      setIosCopied(true);
    }
    try {
      window.location.href = currentUrl.replace(/^https:\/\//, 'x-safari-https://');
    } catch {
      /* ignore */
    }
  };

  const handleCopy = async () => {
    capturePH('inapp_browser_overlay_copy_tapped', {
      source: detected.source,
      platform: detected.platform,
    });
    const ok = await copyUrlToClipboard(currentUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenOther = () => {
    capturePH('inapp_browser_overlay_open_tapped', {
      source: detected.source,
      platform: 'other',
    });
    window.open(currentUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem('ttt_iab_dismissed', '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    capturePH('inapp_browser_overlay_dismissed', {
      source: detected.source,
      platform: detected.platform,
    });
  };

  const title = (t('iab.title') || '').replace('{source}', sourceLabel);
  const body = (t('iab.body') || '').replace('{source}', sourceLabel);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm"
      style={{
        zIndex: 2147483647,
        paddingTop: 'max(20px, env(safe-area-inset-top))',
        paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
        paddingLeft: 20,
        paddingRight: 20,
      }}
    >
      <div className="relative h-full flex flex-col items-center justify-center">
        <div className="absolute top-4 right-4" ref={langRef}>
          <button
            type="button"
            onClick={() => setLangOpen((o) => !o)}
            aria-label="Language"
            className="w-11 h-11 rounded-full bg-white/95 text-[#3A3028] flex items-center justify-center shadow-lg border border-white/60 active:scale-95 touch-manipulation"
          >
            <Globe className="w-5 h-5" />
          </button>
          {langOpen && (
            <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200 min-w-max">
              {['lt', 'en', 'ru', 'es'].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => {
                    setLanguage(lang);
                    setLangOpen(false);
                  }}
                  className={`block w-full px-5 py-3 text-left text-sm transition-colors ${
                    language === lang
                      ? 'bg-[#FF8C5A]/10 text-[#FF8C5A] font-semibold'
                      : 'text-[#3A3028] hover:bg-gray-50'
                  }`}
                >
                  {LANG_LABELS[lang]}
                </button>
              ))}
            </div>
          )}
        </div>

        <div
          className="w-full max-w-sm bg-white rounded-3xl shadow-2xl px-6 pt-2 pb-6 text-center"
          style={{ animation: 'sf-fade-up 0.35s cubic-bezier(0.4,0,0.2,1) both' }}
        >
          <img
            src={timoWaving}
            alt="Timo"
            className="w-32 h-auto mx-auto -mt-14 drop-shadow-md select-none"
            draggable={false}
          />

          <h1 className="text-[#3A3028] text-xl font-bold leading-tight mb-2 mt-2">
            {title}
          </h1>
          <p className="text-[#6B7280] text-sm leading-snug mb-5 px-2">
            {body}
          </p>

          {detected.platform === 'android' && (
            <>
              <button
                type="button"
                onClick={handleOpenAndroid}
                className="block w-full bg-[#FF8C5A] text-white py-4 rounded-2xl font-bold text-base active:scale-95 transition-transform touch-manipulation shadow-lg"
              >
                {t('iab.openChrome')}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="block w-full mt-2 py-3 rounded-2xl text-[#3A3028] font-medium text-sm border border-gray-200 active:scale-95 transition-transform touch-manipulation"
              >
                {copied ? t('iab.copied') : t('iab.copyLink')}
              </button>
              <p className="text-[11px] text-[#9CA3AF] mt-4 leading-snug">
                {t('iab.hintAndroid')}
              </p>
            </>
          )}

          {detected.platform === 'ios' && (
            <>
              <button
                type="button"
                onClick={handleIosCopyAndJump}
                className="block w-full bg-[#FF8C5A] text-white py-4 rounded-2xl font-bold text-base active:scale-95 transition-transform touch-manipulation shadow-lg"
              >
                {iosCopied ? t('iab.iosLinkCopied') : t('iab.iosCopyAndOpen')}
              </button>

              <div className="mt-5 text-left bg-[#FFF7F1] border border-[#FFE0CC] rounded-2xl px-4 py-3.5">
                <div className="flex items-start gap-2.5">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FF8C5A] text-white text-[11px] font-bold flex items-center justify-center mt-0.5">1</div>
                  <p className="text-[13px] text-[#3A3028] leading-snug">{t('iab.iosStep1')}</p>
                </div>
                <div className="flex items-start gap-2.5 mt-2">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FF8C5A] text-white text-[11px] font-bold flex items-center justify-center mt-0.5">2</div>
                  <p className="text-[13px] text-[#3A3028] leading-snug">{t('iab.iosStep2')}</p>
                </div>
                <div className="flex items-start gap-2.5 mt-2">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FF8C5A] text-white text-[11px] font-bold flex items-center justify-center mt-0.5">3</div>
                  <p className="text-[13px] text-[#3A3028] leading-snug">{t('iab.iosStep3')}</p>
                </div>
              </div>
            </>
          )}

          {detected.platform === 'other' && (
            <>
              <button
                type="button"
                onClick={handleOpenOther}
                className="block w-full bg-[#FF8C5A] text-white py-4 rounded-2xl font-bold text-base active:scale-95 transition-transform touch-manipulation shadow-lg"
              >
                {t('iab.openBrowser')}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="block w-full mt-2 py-3 rounded-2xl text-[#3A3028] font-medium text-sm border border-gray-200 active:scale-95 transition-transform touch-manipulation"
              >
                {copied ? t('iab.copied') : t('iab.copyLink')}
              </button>
              <p className="text-[11px] text-[#9CA3AF] mt-4 leading-snug">
                {t('iab.hintOther')}
              </p>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          className="mt-5 inline-flex min-h-[44px] items-center text-white/70 text-xs underline underline-offset-2 px-3 py-2 touch-manipulation"
        >
          {t('iab.continueAnyway')}
        </button>
      </div>
    </div>
  );
}
